// Tipos compartidos entre el frontend y las Pages Functions.
// Booleanos se guardan en SQLite como INTEGER 0/1 -> aca se tipan como number.

// -------------------------------------------------------------- filas de BD
export interface Empresa {
  id: number;
  nombre: string;
  direccion: string | null;
  ciudad: string | null;
  telefono: string | null;
  email: string | null;
  web: string | null;
  notas: string | null;
  activo: number;
}

export interface Categoria {
  id: number;
  nombre: string;
  prefijo: number | null;
}

export interface CategoriaConConteo extends Categoria {
  productos: number;
  iva_min: number | null;
  iva_max: number | null;
}

// ---- Subas ----
export interface SubaPreviewItem {
  producto_id: number;
  codigo: number;
  descripcion: string;
  viejo: number;
  nuevo: number;
}
export interface SubaPreviewLista {
  lista_id: number;
  nombre: string;
  items: SubaPreviewItem[];
}
export interface SubaPreviewRespuesta {
  preview: boolean;
  porcentaje: number;
  listas: SubaPreviewLista[];
}
export interface Suba {
  id: number;
  fecha: string;
  lista_id: number;
  porcentaje: number;
  redondeo: string;
  notas: string | null;
  lista: string;
  lista_desc: string | null;
}

// ---- Informes ----
export interface InformeVentas {
  desde: string | null;
  hasta: string | null;
  por_cliente: Array<{ cliente_id: number; cliente: string; pedidos: number; total: number }>;
  por_fecha: Array<{ fecha: string; pedidos: number; total: number }>;
  top_productos: Array<{ codigo: number; descripcion: string; bultos: number; importe: number }>;
}

// Fila del GET /api/listas/:id/precios
export interface PrecioLista {
  producto_id: number;
  codigo: number;
  descripcion: string;
  empaque: number;
  precio: number;
  categoria_id: number;
  categoria: string;
}

export interface AreaProduccion {
  id: number;
  nombre: string;
}

export interface Producto {
  id: number;
  codigo: number;
  descripcion: string;
  categoria_id: number;
  area_id: number;
  empaque: number;
  iva_tasa: number;
  activo: number;
}

export interface Lista {
  id: number;
  nombre: string;
  descripcion: string | null;
  activa: number;
}

export interface Precio {
  lista_id: number;
  producto_id: number;
  precio: number;
}

export interface Cliente {
  id: number;
  codigo: string;
  nombre: string;
  contacto: string | null;
  lista_id: number;
  direccion: string | null;
  telefono: string | null;
  celular: string | null;
  email: string | null;
  notas: string | null;
  activo: number;
}

// PENDIENTE = lo mando el local desde el celular y todavia no lo confirmo
// administracion: no entra a produccion ni a la cuenta corriente.
export type EstadoPedido = "PENDIENTE" | "CONFIRMADO" | "ANULADO";
export type OrigenPedido = "INTERNO" | "CLIENTE";

/** Momento de la entrega. La cuadra separa la produccion por turno. */
export type Turno = "MANANA" | "TARDE";

export const TURNOS: Array<{ valor: Turno; nombre: string }> = [
  { valor: "MANANA", nombre: "Mañana" },
  { valor: "TARDE", nombre: "Tarde" },
];

export function nombreTurno(t: Turno | string | null | undefined): string {
  return t === "TARDE" ? "Tarde" : t === "MANANA" ? "Mañana" : "";
}

export interface Pedido {
  id: number;
  fecha_entrega: string;
  turno: Turno;
  cliente_id: number;
  empresa_id: number | null;
  lista_id: number;
  total: number;
  descuento: number;
  estado: EstadoPedido;
  origen: OrigenPedido;
  creado_por: string | null;
  confirmado_en: string | null;
  creado_en: string;
}

export interface PedidoLinea {
  id: number;
  pedido_id: number;
  producto_id: number;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
}

// ANULACION es el contra-asiento de una COMPRA cuyo pedido se anulo: el
// movimiento original NO se borra, se compensa. Guarda el monto en positivo y
// resta al sumar, igual que PAGO y DEVOLUCION.
export type TipoMov = "COMPRA" | "DEVOLUCION" | "PAGO" | "FALTANTE" | "ANULACION";

export interface CuentaMov {
  id: number;
  cliente_id: number;
  fecha: string;
  tipo: TipoMov;
  monto: number;
  pedido_id: number | null;
  notas: string | null;
  creado_en: string;
  /** Del pedido que lo origino. null en PAGO, que no tiene pedido. */
  turno: Turno | null;
  fecha_entrega: string | null;
}

// Fila de la vista v_produccion
export interface ProduccionItem {
  fecha_entrega: string;
  turno: Turno;
  producto_id: number;
  codigo: number;
  descripcion: string;
  empaque: number;
  area: string;
  categoria: string;
  bultos: number;
  unidades: number;
}

// Resumen de un pedido del dia
export interface PedidoResumen {
  id: number;
  cliente: string;
  total: number;
  turno: Turno;
}

export interface ProduccionRespuesta {
  fecha: string;
  items: ProduccionItem[];
  pedidos: PedidoResumen[];
}

// -------------------------------------------------------------- DTOs de API
export interface LineaPedidoInput {
  producto_id: number;
  cantidad: number;
  // Override manual del operador (ajuste puntual en mostrador). Si se omite,
  // el servidor usa el precio de la lista del cliente.
  precio_unitario?: number;
}

export interface NuevoPedido {
  cliente_id: number;
  empresa_id?: number | null;
  fecha_entrega?: string;
  descuento?: number;
  lineas: LineaPedidoInput[];
}

export interface MovManual {
  tipo: "PAGO" | "DEVOLUCION" | "FALTANTE";
  monto?: number;                                    // solo PAGO
  items?: Array<{ producto_id: number; cantidad: number }>; // DEVOLUCION / FALTANTE
  fecha?: string;
  notas?: string;
}

// Respuesta de GET /api/pedidos/preparar: todo lo necesario para la pantalla
// de pedido en una sola llamada (catalogo + precios de la lista del cliente).
export interface PreparaPedido {
  cliente: Cliente;
  lista: Lista;
  categorias: Categoria[];
  areas: AreaProduccion[];
  fecha_sugerida: string;
  items: PreparaItem[];
}

export interface PreparaItem {
  producto: Producto;
  precio: number; // precio en la lista del cliente (0 = no disponible)
}

export type RolUsuario = "INTERNO" | "CLIENTE";

/** Un local que el usuario puede gestionar. */
export interface LocalDeUsuario {
  id: number;
  nombre: string;
}

export interface Sesion {
  usuario: string;
  /** Correo de Google con el que entro. */
  email: string | null;
  es_admin: boolean;
  permisos: string[];
  rol: RolUsuario;
  /**
   * Locales que maneja. Solo si rol = 'CLIENTE'. Una misma persona puede
   * gestionar varios, asi que la pantalla de pedido muestra un selector
   * cuando hay mas de uno.
   */
  locales: LocalDeUsuario[];
}

// -------------------------------------------------------- portal del local
/** Acceso web de un cliente (GET /api/clientes/:id/acceso -> { accesos }). */
export interface AccesoWeb {
  id: number;
  usuario: string;
  activo: number;
  /** Correo de Google con el que entra. null = todavia no se lo cargaron. */
  email: string | null;
  /** Cuantos locales maneja: 1 = el acceso propio, 2+ = encargado multi-local. */
  locales: number;
}

/** Respuesta del alta: la clave viaja UNA sola vez y no se guarda en claro. */
export interface AccesoWebCreado {
  usuario: string;
  email: string;
  nuevo: boolean;
}

/** Fila de GET /api/cliente/pedidos. */
export interface PedidoDeLocal {
  id: number;
  fecha_entrega: string;
  turno: Turno;
  total: number;
  estado: EstadoPedido;
  origen: OrigenPedido;
  creado_en: string;
  confirmado_en: string | null;
  cliente_id: number;
  cliente_nombre: string;
}

/** Linea del detalle de un pedido del local. */
export interface LineaDeLocal {
  producto_id: number;
  cantidad: number;
  precio_unitario: number;
  subtotal: number;
  codigo: number;
  descripcion: string;
  empaque: number;
}

/** GET /api/cliente/cuenta. */
export interface CuentaDeLocal {
  cliente_id: number;
  saldo: number;
  movimientos: Array<{
    fecha: string;
    tipo: TipoMov;
    monto: number;
    pedido_id: number | null;
    notas: string | null;
    turno: Turno | null;
    fecha_entrega: string | null;
  }>;
}

/** GET /api/notificaciones. */
export interface Aviso {
  id: number;
  titulo: string;
  cuerpo: string;
  /** Ruta a la que lleva el aviso ('#/mispedidos', '#/pedidos-web'). */
  hash: string | null;
  /** 0 = todavia no lo vio. Con ?todos=1 vienen los dos; si no, solo los 0. */
  leida: number;
  creada_en: string;
}
export interface AvisosRespuesta {
  pendientes: number;
  avisos: Aviso[];
}

/**
 * Cuantos usuarios ACTIVOS puede tener la confiteria, contando los de local y
 * los internos. La cuenta protegida (soporte) NO cuenta. Lo hace cumplir el
 * backend (exigirCupoDeUsuarios); aca esta para que la pantalla muestre
 * "N de 10" con la misma regla.
 */
export const MAX_USUARIOS = 10;

export interface Usuario {
  id: number;
  usuario: string;
  es_admin: number;
  permisos: string; // JSON array en la base
  activo: number;
  rol: RolUsuario;
  /** Correo de Google con el que entra: la unica forma de entrar. null = no puede entrar. */
  email: string | null;
  /** 1 = solo se puede modificar a si mismo (migracion 0109). */
  protegido: number;
  /** Nombres de los locales que maneja, separados por coma. Solo rol CLIENTE. */
  locales: string | null;
  /** Ids de esos locales, separados por coma. */
  locales_ids: string | null;
}

export interface AuditoriaFila {
  id: number;
  fecha_hora: string; // UTC 'YYYY-MM-DD HH:MM:SS'
  usuario: string;
  accion: string;
  entidad: string;
  entidad_id: string | null;
  detalle: string | null;
}

export interface SugeridoRespuesta {
  pedido_id: number | null;
  fecha_entrega: string | null;
  lineas: Array<{ producto_id: number; cantidad: number }>;
}
